import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

import {
  MENU_ITEM_BASE_CLASS,
  MENU_ITEM_ICON_CLASS,
  MENU_ITEM_TONE_CLASS,
  type MenuItemTone,
} from './menu.constant';

export interface MenuItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onSelect'
> {
  /** Figma `Label` */
  children?: ReactNode;
  /** Figma `Leading icon` + `Icon`. 글자 앞 아이콘(`<Icon name="…" size="sm" />`) */
  icon?: ReactNode;
  /** Figma `Shortcut`. 글자 뒤 단축키(`⌘K`) */
  shortcut?: ReactNode;
  /** Figma `tone`. danger = 삭제처럼 되돌릴 수 없는 동작. 기본 default */
  tone?: MenuItemTone;
  /** 고르면 부른다(마우스 · Enter · Space) */
  onSelect?: () => void;
}

/**
 * `_Menu/Item` — 메뉴 한 줄(`role="menuitem"`). 버튼이 아니라 메뉴 항목이다.
 * 포커스는 메뉴가 화살표로 옮긴다(`tabIndex=-1`). hover 는 CSS 가상 클래스.
 */
export function MenuItem({
  children,
  icon,
  shortcut,
  tone = 'default',
  onSelect,
  onClick,
  className,
  type = 'button',
  ...rest
}: MenuItemProps) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event);
    if (event.defaultPrevented) return;
    onSelect?.();
  }

  return (
    <button
      type={type}
      role="menuitem"
      tabIndex={-1}
      data-tone={tone}
      onClick={handleClick}
      {...rest}
      className={cn(MENU_ITEM_BASE_CLASS, MENU_ITEM_TONE_CLASS[tone], className)}
    >
      {icon ? (
        <span
          data-slot="icon"
          aria-hidden="true"
          className={cn('inline-flex shrink-0', MENU_ITEM_ICON_CLASS[tone])}
        >
          {icon}
        </span>
      ) : null}
      <span data-slot="label">{children}</span>
      {shortcut ? (
        <span
          data-slot="shortcut"
          className="shrink-0 typo-caption text-fg-muted group-disabled:text-fg-disabled"
        >
          {shortcut}
        </span>
      ) : null}
    </button>
  );
}
